import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { InputProps } from '@fhs-legacy/universal-components';
import { AutocompleteOptionsDropdown } from 'components/ucl/autocomplete-options-dropdown';
import useLocationPredictions from 'hooks/geolocation/use-location-predictions';
import useDebounceEffect from 'hooks/use-debounce-effect';
import { CustomEventNames, EventTypes, useCRMEventsContext } from 'state/crm-events';

type BaseInputProps = Pick<InputProps, 'value'>;

export interface IAutocompleteAddressInputProps extends BaseInputProps {
  testID: string;
  autoFocus?: boolean;
  label: string;
  addressesOnly?: boolean;
  required: boolean;

  message?: string;
  setMessage?: React.Dispatch<React.SetStateAction<string>>;

  onAddressSelected: (address: string, placeId?: string) => void;
  onInputValueChange: React.Dispatch<React.SetStateAction<string | undefined>>;
  onBlur?(): void;
}

export const AutocompleteAddressInput = React.forwardRef<
  HTMLInputElement,
  IAutocompleteAddressInputProps & { initialFocus?: boolean }
>(
  (
    {
      testID,
      label,
      message,
      addressesOnly = false,
      onAddressSelected = () => {},
      onInputValueChange,
      onBlur,
      setMessage,
      initialFocus = false,
      value = '',
    },
    ref
  ) => {
    const { formatMessage } = useIntl();
    const { logRBIEvent } = useCRMEventsContext();

    /**
     * Retrieve the predictions based on the user's entry and map them to the format that the component prop expects.
     * Only pass on the first 5 predictions.
     */
    const predictions = useLocationPredictions(value, { addressesOnly })?.slice(0, 5);

    const handleDropdownValueSelected = useCallback(
      ({ address, placeId }: any) => {
        onAddressSelected(address, placeId);
        logRBIEvent({
          name: CustomEventNames.MANUAL_LOCATION_SEARCH,
          type: EventTypes.Search,
          attributes: {
            searchInput: value,
            selectedAddress: address,
          },
        });
      },
      [logRBIEvent, onAddressSelected]
    );

    const handleInputValueChange = useCallback(
      (inputValue: string | undefined) => {
        onInputValueChange(inputValue || '');

        if (setMessage) {
          setMessage('');
        }
      },
      [onInputValueChange, setMessage]
    );

    useDebounceEffect(
      () => {
        onInputValueChange(value);
      },
      500,
      [value]
    );

    return (
      <AutocompleteOptionsDropdown
        noPredictionsFoundText={formatMessage({ id: 'noLocationsMatchYourSearch' })}
        testID={testID}
        onSelection={handleDropdownValueSelected}
        predictions={predictions}
        onInputChange={handleInputValueChange}
        onBlur={onBlur}
        value={value}
        label={label}
        errorMessage={message}
        ref={ref}
        clearTextIconTitle={formatMessage({ id: 'clearText' })}
        initialFocus={initialFocus}
      />
    );
  }
);

export default AutocompleteAddressInput;
